/**
 * PDM V2 body/rows option SKU helpers.
 *
 * PDM supplies option labels; Dato supplies M3 segment SKUs; `slug` (`option-N`)
 * is for factory-match filtering only.
 *
 * @see docs/upholstery/pdm/M3_CODE_AND_PDM_V2_SKUS.md
 */

import type {
  OptionValue,
  Step,
  StepConfiguration,
} from "@views/Upholstery/types"
import type { PdmAssetRecord, PdmAssetRowConfiguration } from "../../types"

/** Pattern step key / literal on pattern option — not an M3 segment. */
export const PATTERN_FACTORY_MATCH_KEY = "factory-match"

/** PDM V2 factory-match filter tokens assigned to `slug`, not `sku`. */
export const PDM_FILTER_SLUG_PATTERN = /^option-\d+$/i

export function isPdmFilterSlugSku(sku: string): boolean {
  return PDM_FILTER_SLUG_PATTERN.test(sku.trim())
}

/** Whether a body/rows substep `sku` is a real M3 segment (not filter slug or pattern key). */
export function isM3BodyRowsSkuSegment(sku: string): boolean {
  const cleaned = sku.replace(/\s+/g, "")
  return (
    !!cleaned &&
    cleaned !== PATTERN_FACTORY_MATCH_KEY &&
    !isPdmFilterSlugSku(cleaned)
  )
}

/**
 * First real M3 segment from body or rows substeps (skips PDM filter slugs).
 *
 * For `rows`, pick one segment in stable order (front → middle → back).
 * For `body` (or unspecified), use the first valid SKU — including trim.
 * Most cars store the M3 remainder on trim (e.g. CHE + 24176).
 */
export function getFirstBodyRowsSubstepM3Sku(
  config: StepConfiguration[string] | undefined,
  stepKey?: "body" | "rows"
): string {
  if (!config?.substeps) return ""

  const entries = Object.entries(config.substeps)

  // Rows: pick the first real M3 segment in stable order (front → middle → back).
  // Do not concatenate multiple row SKUs — that could change cars that already
  // had a single intentional segment (e.g. Silverado back = 2305X).
  // Traverse still works when middle alone carries 2408E / 2408F.
  if (stepKey === "rows") {
    const order = ["front", "middle", "back"]
    for (const key of order) {
      const raw = entries
        .find(([k]) => k === key)?.[1]
        ?.sku?.replace(/\s+/g, "")
      if (raw && isM3BodyRowsSkuSegment(raw)) return raw
    }
    for (const [key, substepConfig] of entries) {
      if (order.includes(key)) continue
      const raw = substepConfig?.sku?.replace(/\s+/g, "") ?? ""
      if (raw && isM3BodyRowsSkuSegment(raw)) return raw
    }
    return ""
  }

  for (const [, substepConfig] of entries) {
    if (substepConfig?.sku) {
      const sku = substepConfig.sku.replace(/\s+/g, "")
      if (isM3BodyRowsSkuSegment(sku)) return sku
    }
  }
  return ""
}

/**
 * One body/rows M3 segment for custom M3 assembly.
 *
 * Rows wins when it has a real segment. That:
 * - avoids doubling when trim and rows share the same SKU (Silverado 3500HD)
 * - lets Traverse switch 2408E / 2408F from the middle row
 * - still uses trim when rows has no SKU (Colorado, Suburban, etc.)
 */
export function getPreferredBodyRowsM3Sku(
  configurations: StepConfiguration
): string {
  const rowsSku = getFirstBodyRowsSubstepM3Sku(configurations["rows"], "rows")
  if (rowsSku) return rowsSku
  return getFirstBodyRowsSubstepM3Sku(configurations["body"], "body")
}

export function normalizeOptionMatchKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[/\\]/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

function tokenizeOptionMatchKey(value: string): string[] {
  return normalizeOptionMatchKey(value).split(" ").filter(Boolean)
}

/** 0–1 overlap score for picking the best Dato option when labels differ slightly. */
function optionMatchScore(pdmKey: string, datoKey: string): number {
  const pdmTokens = tokenizeOptionMatchKey(pdmKey)
  const datoTokens = tokenizeOptionMatchKey(datoKey)
  if (!pdmTokens.length || !datoTokens.length) return 0

  const pdmSet = new Set(pdmTokens)
  let shared = 0
  for (const token of datoTokens) {
    if (pdmSet.has(token)) shared++
  }

  return shared / Math.max(pdmTokens.length, datoTokens.length)
}

/**
 * Match a PDM/Dato label to a Dato `optionValues` entry (exact → normalized → token overlap).
 */
export function findDatoOptionMatch(
  datoOpts: OptionValue[],
  needle: string
): OptionValue | undefined {
  const trimmed = needle.trim()
  if (!trimmed || !datoOpts.length) return undefined

  const exact = datoOpts.find(
    (d) => d.optionValue?.trim() === trimmed || d.optionName?.trim() === trimmed
  )
  if (exact) return exact

  const normalized = normalizeOptionMatchKey(trimmed)
  const normalizedExact = datoOpts.find((d) => {
    const dName = normalizeOptionMatchKey(d.optionName ?? "")
    const dValue = normalizeOptionMatchKey(d.optionValue ?? "")
    return dName === normalized || dValue === normalized
  })
  if (normalizedExact) return normalizedExact

  let best: OptionValue | undefined
  let bestScore = 0
  for (const datoOpt of datoOpts) {
    const candidates = [datoOpt.optionName ?? "", datoOpt.optionValue ?? ""]
    for (const candidate of candidates) {
      const score = optionMatchScore(trimmed, candidate)
      if (score > bestScore) {
        bestScore = score
        best = datoOpt
      }
    }
  }

  // Require most tokens to align so "60-40" does not pick the storage variant.
  return bestScore >= 0.5 ? best : undefined
}

/**
 * Filter Dato PDM Assets to only include rows matching the car's PDM attribute_values.
 * This ensures we only show row configurations that PDM says are available for this specific car.
 */
export function filterDatoPdmAssetsByPdmValues(
  datoAssets: PdmAssetRecord[] | null,
  pdmRowOptions: {
    front: OptionValue[]
    back: OptionValue[]
    middle: OptionValue[]
  }
): PdmAssetRecord[] | null {
  if (!datoAssets?.length) return null

  const filtered: PdmAssetRecord[] = []

  // Create a map of row types to their PDM values
  const rowTypeMap: Record<string, string[]> = {
    "Front Row": pdmRowOptions.front.map((opt) => opt.optionValue ?? ""),
    "Back Row": pdmRowOptions.back.map((opt) => opt.optionValue ?? ""),
    "Middle Row": pdmRowOptions.middle.map((opt) => opt.optionValue ?? ""),
  }

  datoAssets.forEach((asset) => {
    const rowType = asset.title?.trim()
    if (!rowType || !rowTypeMap[rowType]) return

    const pdmValues = rowTypeMap[rowType]
    if (!pdmValues.length) return

    // Filter rows to only include those matching PDM values
    const filteredRows =
      asset.rows?.filter((row) => {
        const rowValue = row.value?.trim() || ""
        // Try exact match first
        if (pdmValues.some((pdmVal) => pdmVal.trim() === rowValue)) {
          return true
        }
        // Try normalized match
        const normalizedRowValue = normalizeOptionMatchKey(rowValue)
        return pdmValues.some(
          (pdmVal) => normalizeOptionMatchKey(pdmVal) === normalizedRowValue
        )
      }) || []

    if (filteredRows.length > 0) {
      filtered.push({
        ...asset,
        rows: filteredRows,
      })
    }
  })

  return filtered.length > 0 ? filtered : null
}

export function getDatoSubstepOptionValues(
  datoStep: Step | undefined,
  substepKey: string
): OptionValue[] | undefined {
  return datoStep?.substeps?.find((ss) => ss.key === substepKey)?.category
    ?.optionValues
}

/**
 * Copy Dato M3 segment SKUs onto PDM-built body/rows options.
 * PDM only supplies labels; `slug` stays `option-N` for factory-match filters.
 */
export function overlayDatoSkusOnPdmOptions(
  pdmOpts: OptionValue[],
  datoOpts: OptionValue[] | undefined
): OptionValue[] {
  if (!datoOpts?.length) {
    return pdmOpts
  }

  return pdmOpts.map((pdmOpt) => {
    const pdmLabel = pdmOpt.optionName ?? pdmOpt.optionValue ?? ""
    const datoMatch =
      findDatoOptionMatch(datoOpts, pdmLabel) ??
      (pdmOpt.optionValue && pdmOpt.optionValue !== pdmLabel
        ? findDatoOptionMatch(datoOpts, pdmOpt.optionValue)
        : undefined)

    const datoSku = datoMatch?.sku?.trim() ?? ""
    if (datoSku && !isPdmFilterSlugSku(datoSku)) {
      return { ...pdmOpt, sku: datoSku }
    }
    return pdmOpt
  })
}

/**
 * Row options: when direct PDM→Dato label match misses, use the same pdmAsset row
 * match as image overlay, then resolve SKU from per-vehicle Dato optionValues.
 */
export function overlayDatoSkusViaPdmAssetRows(
  pdmOpts: OptionValue[],
  rowType: string,
  datoOpts: OptionValue[] | undefined,
  datoAssets: PdmAssetRecord[] | undefined | null
): OptionValue[] {
  if (!datoOpts?.length || !datoAssets?.length) return pdmOpts

  return pdmOpts.map((pdmOpt) => {
    const existingSku = pdmOpt.sku?.trim() ?? ""
    if (existingSku && isM3BodyRowsSkuSegment(existingSku)) return pdmOpt

    const pdmValue = pdmOpt.optionValue ?? pdmOpt.optionName ?? ""
    const assetMatch = findDatoRowAssetMatch(pdmValue, rowType, datoAssets)
    if (!assetMatch?.value) return pdmOpt

    const datoMatch = findDatoOptionMatch(datoOpts, assetMatch.value)
    const datoSku = datoMatch?.sku?.trim() ?? ""
    if (datoSku && !isPdmFilterSlugSku(datoSku)) {
      return { ...pdmOpt, sku: datoSku }
    }
    return pdmOpt
  })
}

/**
 * Find matching Dato row configuration within the appropriate pdmAsset.
 * First finds the pdmAsset by matching title with rowType (e.g., "Front Row", "Back Row", "Middle Row").
 * Then finds the specific row configuration by matching pdmAttributeValue with row.value.
 */
export function findDatoRowAssetMatch(
  pdmAttributeValue: string,
  rowType: string, // "Front Row", "Back Row", or "Middle Row"
  datoAssets: PdmAssetRecord[]
): PdmAssetRowConfiguration | undefined {
  const trimmed = pdmAttributeValue.trim()
  if (!trimmed) return undefined

  // Step 1: Find the pdmAsset that matches the row type (Front Row, Back Row, Middle Row)
  const matchingAsset = datoAssets.find(
    (asset) => asset.title?.trim() === rowType.trim()
  )

  if (!matchingAsset?.rows?.length) {
    console.warn(
      `[findDatoRowAssetMatch] No pdmAsset found with title "${rowType}"`
    )
    return undefined
  }

  const datoRows = matchingAsset.rows

  // Step 2: Find exact match on row value
  const exactMatch = datoRows.find((row) => row.value?.trim() === trimmed)
  if (exactMatch) return exactMatch

  // Step 3: Normalized match on value, title, or key
  const normalized = normalizeOptionMatchKey(trimmed)
  const normalizedMatch = datoRows.find((row) => {
    const rowValue = normalizeOptionMatchKey(row.value ?? "")
    const rowTitle = normalizeOptionMatchKey(row.title ?? "")
    const rowKey = normalizeOptionMatchKey(row.key ?? "")
    return (
      rowValue === normalized ||
      rowTitle === normalized ||
      rowKey === normalized
    )
  })
  if (normalizedMatch) return normalizedMatch

  // Step 4: Partial match - check if PDM value starts with or is contained in Dato value
  const partialMatch = datoRows.find((row) => {
    const rowValue = normalizeOptionMatchKey(row.value ?? "")
    const rowTitle = normalizeOptionMatchKey(row.title ?? "")
    return (
      rowValue.includes(normalized) ||
      normalized.includes(rowValue) ||
      rowTitle.includes(normalized) ||
      normalized.includes(rowTitle)
    )
  })
  if (partialMatch) return partialMatch

  console.warn(
    `[findDatoRowAssetMatch] No match found for "${trimmed}" in ${rowType} rows`
  )
  return undefined
}

/**
 * Copy Dato PDM Asset row images onto PDM-built row options.
 * Matches by finding the correct pdmAsset (by title) then the row configuration (by value).
 * Images come only from `pdmAssetRowsQuery` — never local row assets.
 */
export function overlayDatoRowAssetsOnPdmOptions(
  pdmOpts: OptionValue[],
  rowType: string, // "Front Row", "Back Row", or "Middle Row"
  datoAssets: PdmAssetRecord[] | undefined | null
): OptionValue[] {
  if (!datoAssets?.length) {
    console.warn(
      "[overlayDatoRowAssetsOnPdmOptions] No Dato pdmAssets available for matching"
    )
    return pdmOpts
  }

  // Find the asset for this row type
  const matchingAsset = datoAssets.find(
    (asset) => asset.title?.trim() === rowType.trim()
  )

  if (!matchingAsset) {
    console.warn(
      `[overlayDatoRowAssetsOnPdmOptions] No pdmAsset found with title "${rowType}"`
    )
    return pdmOpts
  }

  return pdmOpts.map((pdmOpt) => {
    const pdmValue = pdmOpt.optionValue ?? pdmOpt.optionName ?? ""
    const datoMatch = findDatoRowAssetMatch(pdmValue, rowType, datoAssets)
    const responsiveImage = datoMatch?.image?.responsiveImage

    if (!responsiveImage?.src) {
      return pdmOpt
    }

    return {
      ...pdmOpt,
      optionImage: {
        responsiveImage: {
          src: responsiveImage.src,
        },
      },
    }
  })
}
